import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { NavLink } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useShipmentLiveStatus } from "@/hooks/useShipmentLiveStatus";
import { useToast } from "@/hooks/useToast";
import {
  useScreenWakeLock,
  KEEP_AWAKE_OPTIONS,
  keepAwakeLabel,
  type KeepAwakeMinutes,
} from "@/hooks/useScreenWakeLock";
import { enablePush, getPushState, type PushEnvState } from "@/services/messaging";
import { useSpace } from "@/hooks/useSpace";
import { isHomeKey, menuPath, navFor, type NavItem } from "@/domain/menus";

/**
 * MobileMenuSheet — MO(≤659px) 전용 바텀시트 메뉴.
 *
 * "메뉴" 탭을 누르면 아래에서 위로 슬라이드 업. 상단 grab 핸들을 아래로 끌거나
 * "메뉴"(X) 탭을 다시 누르면 닫힘. 내용은 NAV(menus.ts) 그룹을 2열 그리드로 배치
 * (홈은 중앙 탭이라 제외, 게이트 권한 보유 메뉴만, LIVE 마커는 배지로).
 * CSS 는 components.css 의 `.mob-menu*` (base display:none + MO 미디어쿼리에서 표시).
 */
interface MobileMenuSheetProps {
  open: boolean;
  onClose: () => void;
}

interface MenuGroup {
  label: string;
  items: NavItem[];
}

const CLOSE_THRESHOLD = 90; // px — 이만큼 아래로 끌면 닫힘

export function MobileMenuSheet({ open, onClose }: MobileMenuSheetProps) {
  // B′: 메뉴 시트도 지금 공간의 메뉴(navFor)
  const { space, biz, hasPermIn } = useSpace();
  // 출하조회 라이브 배지 점등 — 오늘 출하 시작~마무리 사이에만 초록(링), 그 외 회색
  const liveStatus = useShipmentLiveStatus();
  const [dragY, setDragY] = useState(0);
  const drag = useRef<{ startY: number; active: boolean }>({ startY: 0, active: false });

  // 닫히면 drag 잔상 초기화
  useEffect(() => {
    if (!open) setDragY(0);
  }, [open]);

  // NAV → 권한 필터 + 그룹화 (홈 제외, 빈 그룹 제거)
  const groups: MenuGroup[] = [];
  let cur: MenuGroup | null = null;
  for (const n of navFor(space, biz)) {
    if (n.type === "group") {
      cur = { label: n.label, items: [] };
      groups.push(cur);
      continue;
    }
    if (isHomeKey(n.key)) continue;
    if (n.gate && !n.gate.some((k) => hasPermIn(k))) continue;
    if (!cur) {
      cur = { label: "", items: [] };
      groups.push(cur);
    }
    cur.items.push(n);
  }
  const visibleGroups = groups.filter((g) => g.items.length > 0);

  // 시트는 항상 DOM 에 존재(닫힘 시 translateY(100%) 로 화면 밖). 그래야 열 때
  // 매번 translateY(100%) → 0 으로 슬라이드업이 보장됨(mount 타이밍 문제 제거).

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    drag.current = { startY: e.clientY, active: true };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!drag.current.active) return;
    setDragY(Math.max(0, e.clientY - drag.current.startY));
  }
  function onPointerUp() {
    if (!drag.current.active) return;
    drag.current.active = false;
    if (dragY > CLOSE_THRESHOLD) onClose();
    setDragY(0);
  }

  return (
    <div className={`mob-menu ${open ? "is-open" : ""}`} aria-hidden={!open}>
      <div className="mob-menu-scrim" onClick={onClose} />
      <div
        className="mob-menu-sheet"
        role="dialog"
        aria-label="메뉴"
        style={dragY ? { transform: `translateY(${dragY}px)`, transition: "none" } : undefined}
      >
        <div
          className="mob-menu-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="mob-menu-grab-bar" />
        </div>
        <div className="mob-menu-scroll">
          <div className="mob-menu-grid">
            {visibleGroups.map((g, gi) => (
              <section className="mob-menu-group" key={g.label || gi}>
                {g.label && <div className="mob-menu-group-label">{g.label}</div>}
                <div className="mob-menu-items">
                  {g.items.map((it) => (
                    <NavLink
                      key={it.key}
                      to={menuPath(it.key, space, biz)}
                      className={({ isActive }) => `mob-menu-item ${isActive ? "is-active" : ""}`}
                      onClick={onClose}
                    >
                      <span className="mob-menu-item-ico"><Icon name={it.icon} size={20} /></span>
                      <span className="mob-menu-item-label">{it.label}</span>
                      {it.marker === "live" && (
                        <span className={`mob-menu-live ${liveStatus === "live" ? "is-on" : "is-off"}`}>
                          <span className="mob-menu-live-dot" aria-hidden />
                          라이브
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </section>
            ))}
          </div>
          <MobileMenuFooter open={open} />
        </div>
      </div>
    </div>
  );
}

/**
 * MO 메뉴 하단 footer — "알림 활성화"(관리자) + "화면 유지"(모두) 구성.
 *
 * 알림이 정상(default/granted)이면 알림·화면유지를 좌우 절반(2×1)으로 나란히 두고,
 * 알림이 차단/미설치/미지원이면 알림은 풀폭 안내 카드 + 화면유지는 그 아래 풀폭으로
 * 떨어뜨린다. 비관리자는 알림 슬롯이 없어 화면유지만 풀폭으로 표시.
 *
 * 화면 유지(Wake Lock) 설정은 footer 가 항상 mount(open 무관) 되므로 여기서 훅을
 * 호출해 효과를 상시 유지한다. 푸시 발송이 현재 관리자 전용이라 알림은 관리자만 노출.
 */
function MobileMenuFooter({ open }: { open: boolean }) {
  const { user } = useAuth();
  const toast = useToast();
  const uid = user?.uid ?? null;
  const isAdmin = !!user?.isAdmin;
  const wake = useScreenWakeLock();

  const [state, setState] = useState<PushEnvState | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setState(await getPushState());
  }, []);

  // 메뉴가 열릴 때마다 권한 상태 재조회(기기 설정에서 바꾸고 돌아온 경우 반영).
  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const showPush = isAdmin && !!uid;

  async function activate() {
    if (!uid || busy) return;
    setBusy(true);
    try {
      const res = await enablePush(uid);
      if (res.ok) toast.success("이 기기에서 알림을 받도록 설정했습니다.");
      else toast.error(res.reason ?? "알림 설정에 실패했습니다.");
    } catch (e) {
      toast.error(`처리 실패: ${(e as Error).message}`);
    } finally {
      setBusy(false);
      void refresh(); // 요청 후 권한이 denied로 바뀌었을 수 있어 갱신
    }
  }

  // 푸시 표시 모드 (우선순위: 미설치 > 미지원 > 차단 > 허용됨 > 기본)
  const needsStandalone = !!state?.needsStandalone;
  const unsupported = state != null && !state.supported && !needsStandalone;
  const denied = !!state?.supported && state.permission === "denied";
  const granted = !!state?.supported && state.permission === "granted";

  // 절반(compact)으로 나란히 둘 수 있는 정상 상태 = 차단/미설치/미지원이 아닐 때
  const sideBySide = showPush && !denied && !needsStandalone && !unsupported;
  const pushDisabled = busy || state == null;

  // 알림 compact 보조 텍스트
  let pushSub = "활성화";
  if (state == null) pushSub = "확인 중…";
  else if (busy) pushSub = "설정 중…";
  else if (granted) pushSub = "켜짐";

  return (
    <div className="mob-menu-footer">
      {sideBySide ? (
        <div className="mmf-row">
          <button
            type="button"
            className={`mmf-btn${granted ? " is-on" : ""}`}
            disabled={pushDisabled}
            onClick={() => void activate()}
          >
            <span className="mmf-btn-ico">
              <Icon name={granted ? "check" : "bell"} size={20} />
            </span>
            <span className="mmf-btn-label">알림</span>
            <span className="mmf-btn-val">{pushSub}</span>
          </button>
          <WakeButton mode="compact" wake={wake} />
        </div>
      ) : (
        <>
          {showPush && denied && (
            <>
              <div className="mob-menu-ntf is-blocked" role="status">
                <span className="mob-menu-ntf-ico">
                  <Icon name="bell" size={18} />
                </span>
                <span className="mob-menu-ntf-text">
                  <span className="mob-menu-ntf-label">알림이 차단되어 있습니다</span>
                  <span className="mob-menu-ntf-desc">
                    이 기기에서 알림을 거부한 상태라 버튼으로는 다시 켤 수 없습니다.
                  </span>
                </span>
              </div>
              <ol className="mob-menu-ntf-guide">
                <li>
                  <b>설정에서 켜기</b> — iOS 설정 → 알림 → 이 앱(중앙ERP) → ‘알림 허용’ 켜기
                </li>
                <li>
                  <b>목록에 없으면 재설치</b> — 홈 화면 아이콘을 길게 눌러 삭제 → Safari에서 다시 열어
                  공유 → ‘홈 화면에 추가’ → 알림 다시 켜기
                </li>
              </ol>
            </>
          )}
          {showPush && (needsStandalone || unsupported) && (
            <button
              type="button"
              className="mob-menu-ntf"
              disabled
            >
              <span className="mob-menu-ntf-ico">
                <Icon name="bell" size={18} />
              </span>
              <span className="mob-menu-ntf-text">
                <span className="mob-menu-ntf-label">알림 활성화</span>
                <span className="mob-menu-ntf-desc">
                  {needsStandalone
                    ? "Safari 공유 → ‘홈 화면에 추가’ 후 설치된 앱에서 눌러 주세요"
                    : "이 기기는 웹 푸시를 지원하지 않습니다"}
                </span>
              </span>
            </button>
          )}
          <WakeButton mode="wide" wake={wake} />
        </>
      )}
    </div>
  );
}

/**
 * 화면 유지(Wake Lock) 시간 설정 버튼 + 옵션 팝오버.
 *  - compact: 알림과 나란히 두는 절반 칸(세로형)
 *  - wide:    알림이 풀폭이거나 비관리자일 때 단독 풀폭(가로형)
 * 탭하면 위로 옵션(기본값/1·3·5·10분)이 펼쳐지고, 고르면 즉시 저장(기기별 localStorage).
 */
function WakeButton({
  mode,
  wake,
}: {
  mode: "compact" | "wide";
  wake: ReturnType<typeof useScreenWakeLock>;
}) {
  const { minutes, setOption, supported } = wake;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [open]);

  const valLabel = supported ? keepAwakeLabel(minutes) : "미지원";
  const on = supported && minutes > 0;

  function pick(m: KeepAwakeMinutes) {
    setOption(m);
    setOpen(false);
  }

  const popover = open && supported && (
    <div className="mmf-pop" role="menu" aria-label="화면 유지 시간">
      {KEEP_AWAKE_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          role="menuitemradio"
          aria-checked={o.value === minutes}
          className={`mmf-pop-item${o.value === minutes ? " is-sel" : ""}`}
          onClick={() => pick(o.value)}
        >
          <span>{o.label}</span>
          {o.value === minutes && <Icon name="check" size={16} />}
        </button>
      ))}
    </div>
  );

  if (mode === "compact") {
    return (
      <div className="mmf-wake" ref={ref}>
        <button
          type="button"
          className={`mmf-btn${on ? " is-on" : ""}`}
          disabled={!supported}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="mmf-btn-ico">
            <Icon name="monitor" size={20} />
          </span>
          <span className="mmf-btn-label">화면 유지</span>
          <span className="mmf-btn-val">
            {valLabel}
            {supported && <Icon name="chevD" size={14} />}
          </span>
        </button>
        {popover}
      </div>
    );
  }

  return (
    <div className="mmf-wake is-wide" ref={ref}>
      <button
        type="button"
        className={`mob-menu-ntf${on ? " is-on" : ""}`}
        disabled={!supported}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="mob-menu-ntf-ico">
          <Icon name="monitor" size={18} />
        </span>
        <span className="mob-menu-ntf-text">
          <span className="mob-menu-ntf-label">화면 유지</span>
          <span className="mob-menu-ntf-desc">
            {supported
              ? "마지막 터치 후 선택한 시간 동안 화면이 꺼지지 않습니다"
              : "이 기기는 화면 유지를 지원하지 않습니다"}
          </span>
        </span>
        <span className="mmf-wake-val">
          {valLabel}
          {supported && <Icon name="chevD" size={14} />}
        </span>
      </button>
      {popover}
    </div>
  );
}
